// src/components/SalaryCalculator.js
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  useMediaQuery,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Avatar,
  alpha,
  Stack,
  Snackbar,
  useTheme,
  LinearProgress,
  Autocomplete,
  Popper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as AccessTimeIcon,
  TrendingDown as TrendingDownIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  RemoveCircle as DeductIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import Sidebar from '../components/sidebars/Sidebar';
import TopNav from '../components/top_bars/TopNav';

// ==================== CONSTANTS ====================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

// ==================== HELPER FUNCTIONS ====================

/**
 * Sanitize string input to prevent XSS attacks
 */
const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/[<>]/g, '')
    .replace(/[&]/g, '&amp;')
    .replace(/["]/g, '&quot;')
    .replace(/[']/g, '&#x27;')
    .replace(/[/]/g, '&#x2F;');
};

/**
 * Format currency for Nigerian Naira
 */
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '₦0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Format date safely
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG');
  } catch (error) {
    console.error('[SalaryCalculator] Date formatting error:', error);
    return 'N/A';
  }
};

/**
 * Log security events
 */
const logSecurityEvent = (eventType, details) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  console.debug(`[SECURITY] ${eventType}:`, logEntry);
};

/**
 * Get numeric status from deduction
 */
const getNumericStatus = (deduction) => {
  return Number(deduction?.deduction_status);
};

/**
 * Get status text
 */
const getStatusText = (status) => {
  const numStatus = Number(status);
  if (numStatus === 0) return 'Pending';
  if (numStatus === 1) return 'Approved';
  if (numStatus === 2) return 'Rejected';
  return 'Unknown';
};

/**
 * Get status color
 */
const getStatusColor = (status) => {
  const numStatus = Number(status);
  if (numStatus === 0) return '#ff9800';
  if (numStatus === 1) return '#4caf50';
  if (numStatus === 2) return '#f44336';
  return '#757575';
};

// ==================== MAIN COMPONENT ====================
const SalaryCalculator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(1);
  
  // Employee selection states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const searchDebounceRef = useRef(null);
  
  // Deductions state
  const [employeeDeductions, setEmployeeDeductions] = useState([]);
  const [loadingDeductions, setLoadingDeductions] = useState(false);
  const [selectedDeductionMonth, setSelectedDeductionMonth] = useState(new Date().getMonth() + 1);
  const [selectedDeductionYear, setSelectedDeductionYear] = useState(new Date().getFullYear());
  const [showDeductionsDialog, setShowDeductionsDialog] = useState(false);
  
  // Calculation state - track if calculation has been performed
  const [calculationPerformed, setCalculationPerformed] = useState(false);
  const [lastCalculationResult, setLastCalculationResult] = useState(null);
  
  // Form state for both staff types
  const [formData, setFormData] = useState({
    staff_id: '',
    staff_name: '',
    staff_phone: '',
    staff_email: '',
    staff_department: '',
    base_salary: '',
    total_late_minutes: '',
    payroll_month: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
    payroll_month_num: new Date().getMonth() + 1,
    payroll_year: new Date().getFullYear(),
    staff_type: 'new',
    // Old staff specific
    custom_working_days: '',
    custom_hours_per_day: '',
    // New staff specific
    employment_start_date: new Date().toISOString().split('T')[0],
  });
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [useCustomHours, setUseCustomHours] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedSections, setExpandedSections] = useState({
    workingDays: true,
    rates: false,
    lateness: false,
    deduction: false,
    dailyBreakdown: false,
    salaryDeductions: false,
  });

  // Recent calculations state
  const [recentCalculations, setRecentCalculations] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  
  // Apply deduction state
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyRemark, setApplyRemark] = useState('');
  const [applyingDeduction, setApplyingDeduction] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
    console.debug('[SalaryCalculator] Sidebar toggled:', !sidebarOpen);
  };

  // Load recent calculations from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSalaryCalculations');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setRecentCalculations(parsed.slice(0, 5));
        console.debug('[SalaryCalculator] Loaded recent calculations:', parsed.length);
      } catch (e) {
        console.error('[SalaryCalculator] Error loading recent calculations:', e);
      }
    }
  }, []);

  // Save calculation to recent
  const saveToRecent = useCallback((calculation) => {
    const newRecent = [
      {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        staff_name: calculation.staff_name,
        staff_id: calculation.staff_id,
        staff_type: calculation.staff_type,
        base_salary: calculation.base_salary,
        final_salary: calculation.final_salary,
        payroll_month: calculation.payroll_month,
        prorated_deduction_amount: calculation.prorated_deduction_amount || 0,
        lateness_deduction_amount: calculation.lateness_deduction_amount || 0,
      },
      ...recentCalculations.filter(c => c.staff_name !== calculation.staff_name)
    ].slice(0, 5);
    
    setRecentCalculations(newRecent);
    localStorage.setItem('recentSalaryCalculations', JSON.stringify(newRecent));
    console.debug('[SalaryCalculator] Saved to recent calculations');
  }, [recentCalculations]);

  // ==================== EMPLOYEE SEARCH FUNCTIONS ====================
  
  /**
   * Search employees by name or phone
   */
  const searchEmployees = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    
    try {
      console.debug(`[SalaryCalculator] Searching employees with term: "${searchTerm}"`);
      
      const response = await fetch(`${API_BASE_URL}/update_salary.php?type=all`);
      const data = await response.json();
      
      if (data.success && data.data) {
        const allEmployees = data.data;
        const searchLower = searchTerm.toLowerCase();
        
        const filtered = allEmployees.filter(emp => 
          (emp.full_name || emp.surname + ' ' + emp.other_names || '').toLowerCase().includes(searchLower) ||
          (emp.surname || '').toLowerCase().includes(searchLower) ||
          (emp.other_names || '').toLowerCase().includes(searchLower) ||
          (emp.phone || '').includes(searchTerm) ||
          (emp.email || '').toLowerCase().includes(searchLower) ||
          (emp.staff_id || '').toLowerCase().includes(searchLower)
        );
        
        setSearchResults(filtered);
        console.debug(`[SalaryCalculator] Found ${filtered.length} employees matching "${searchTerm}"`);
        
        if (filtered.length === 0) {
          setSearchError('No employees found matching your search');
        }
      } else {
        setSearchResults([]);
        setSearchError('Failed to load employee list');
      }
    } catch (error) {
      console.error('[SalaryCalculator] Error searching employees:', error);
      setSearchResults([]);
      setSearchError('Error searching for employees');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  /**
   * Handle employee selection from autocomplete
   */
  const handleEmployeeSelect = async (event, employee) => {
    if (employee) {
      console.debug(`[SalaryCalculator] Employee selected: ${employee.full_name || employee.surname + ' ' + employee.other_names} (${employee.phone})`);
      
      setSelectedEmployee(employee);
      setSearchInput(`${employee.full_name || employee.surname + ' ' + employee.other_names} (${employee.phone})`);
      setSearchResults([]);
      setSearchError('');
      
      setCalculationPerformed(false);
      setLastCalculationResult(null);
      setResult(null);
      
      await fetchEmployeeDeductions(employee.id || employee.staff_id, selectedDeductionMonth, selectedDeductionYear);
      
      setFormData(prev => ({
        ...prev,
        staff_id: employee.staff_id || employee.id || '',
        staff_name: employee.full_name || `${employee.surname || ''} ${employee.other_names || ''}`.trim(),
        staff_phone: employee.phone || '',
        staff_email: employee.email || '',
        staff_department: employee.department || '',
        base_salary: employee.salary_amount || employee.base_salary || '',
      }));
      
      logSecurityEvent('EMPLOYEE_SELECTED', { staff_id: employee.staff_id, staff_name: employee.full_name });
    } else {
      handleClearSelection();
    }
  };

  /**
   * Clear employee selection
   */
  const handleClearSelection = () => {
    console.debug('[SalaryCalculator] Clearing employee selection');
    setSelectedEmployee(null);
    setSearchInput('');
    setEmployeeDeductions([]);
    setCalculationPerformed(false);
    setLastCalculationResult(null);
    setResult(null);
    setFormData(prev => ({
      ...prev,
      staff_id: '',
      staff_name: '',
      staff_phone: '',
      staff_email: '',
      staff_department: '',
      base_salary: '',
    }));
    setSearchResults([]);
    setSearchError('');
  };

  /**
   * Fetch employee deductions for a specific month/year
   */
  const fetchEmployeeDeductions = useCallback(async (employeeId, month, year) => {
    if (!employeeId) {
      setEmployeeDeductions([]);
      return;
    }

    setLoadingDeductions(true);
    
    try {
      console.debug(`[SalaryCalculator] Fetching deductions for employee ${employeeId}, period: ${month}/${year}`);
      
      const response = await fetch(
        `${API_BASE_URL}/salary_deductions.php?employee_id=${employeeId}&month=${month}&year=${year}&type=summary`
      );
      const data = await response.json();
      
      if (data.success && data.data) {
        const processedDeductions = (data.data.all_deductions || []).map(item => ({
          ...item,
          deduction_status: Number(item.deduction_status)
        }));
        setEmployeeDeductions(processedDeductions);
        console.debug(`[SalaryCalculator] Found ${processedDeductions.length} deductions for period`);
      } else {
        setEmployeeDeductions([]);
      }
    } catch (error) {
      console.error('[SalaryCalculator] Error fetching deductions:', error);
      setEmployeeDeductions([]);
    } finally {
      setLoadingDeductions(false);
    }
  }, []);

  /**
   * Handle month/year change for deductions
   */
  const handleDeductionPeriodChange = async (month, year) => {
    setSelectedDeductionMonth(month);
    setSelectedDeductionYear(year);
    
    if (selectedEmployee && (selectedEmployee.id || selectedEmployee.staff_id)) {
      await fetchEmployeeDeductions(selectedEmployee.id || selectedEmployee.staff_id, month, year);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    if (searchInput.trim().length >= 2 && !selectedEmployee) {
      searchDebounceRef.current = setTimeout(() => {
        searchEmployees(searchInput);
      }, 400);
    } else if (searchInput.trim().length < 2) {
      setSearchResults([]);
      setSearchError('');
    }
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput, selectedEmployee, searchEmployees]);

  // Fetch deductions when tab changes or employee is selected
  useEffect(() => {
    if (selectedEmployee && (selectedEmployee.id || selectedEmployee.staff_id)) {
      fetchEmployeeDeductions(
        selectedEmployee.id || selectedEmployee.staff_id,
        formData.payroll_month_num,
        formData.payroll_year
      );
    }
  }, [selectedEmployee, formData.payroll_month_num, formData.payroll_year, fetchEmployeeDeductions]);

  // ==================== FORM HANDLERS ====================
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const newStaffType = newValue === 0 ? 'old' : 'new';
    setFormData(prev => ({
      ...prev,
      staff_type: newStaffType,
      custom_working_days: newValue === 0 ? prev.custom_working_days : '',
      custom_hours_per_day: '',
      employment_start_date: newValue === 1 ? prev.employment_start_date : new Date().toISOString().split('T')[0],
    }));
    setResult(null);
    setError(null);
    setCalculationPerformed(false);
    setLastCalculationResult(null);
    setUseCustomHours(false);
    console.debug('[SalaryCalculator] Tab changed to:', newStaffType);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (result) setResult(null);
    if (error) setError(null);
    setCalculationPerformed(false);
    console.debug(`[SalaryCalculator] Input changed: ${name} = ${value}`);
  };

  const handlePayrollMonthChange = (e) => {
    const monthNum = parseInt(e.target.value);
    const monthName = MONTHS.find(m => m.value === monthNum)?.label || 'January';
    setFormData(prev => ({
      ...prev,
      payroll_month_num: monthNum,
      payroll_month: `${monthName} ${prev.payroll_year}`,
    }));
    setCalculationPerformed(false);
    setResult(null);
    console.debug(`[SalaryCalculator] Payroll month changed to: ${monthName}`);
  };

  const handlePayrollYearChange = (e) => {
    const year = parseInt(e.target.value);
    const monthName = MONTHS.find(m => m.value === formData.payroll_month_num)?.label || 'January';
    setFormData(prev => ({
      ...prev,
      payroll_year: year,
      payroll_month: `${monthName} ${year}`,
    }));
    setCalculationPerformed(false);
    setResult(null);
    console.debug(`[SalaryCalculator] Payroll year changed to: ${year}`);
  };

  const validateForm = () => {
    if (!selectedEmployee && !formData.staff_name.trim()) {
      setError('Please select an employee from search or enter staff name');
      return false;
    }
    
    if (selectedEmployee) {
      if (!formData.base_salary || parseFloat(formData.base_salary) <= 0) {
        setError('Employee salary not found. Please ensure employee has a valid salary.');
        return false;
      }
    } else {
      if (!formData.staff_name.trim()) {
        setError('Please enter staff name');
        return false;
      }
      if (!formData.base_salary || parseFloat(formData.base_salary) <= 0) {
        setError('Please enter a valid base salary greater than 0');
        return false;
      }
    }
    
    if (formData.total_late_minutes && parseFloat(formData.total_late_minutes) < 0) {
      setError('Late minutes cannot be negative');
      return false;
    }
    
    if (activeTab === 1) {
      if (!formData.employment_start_date) {
        setError('Please select employment start date for new staff');
        return false;
      }
      const startDate = new Date(formData.employment_start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate > today) {
        setError('Employment start date cannot be in the future');
        return false;
      }
    } else {
      if (formData.custom_working_days && parseFloat(formData.custom_working_days) <= 0) {
        setError('Working days must be greater than 0');
        return false;
      }
      if (formData.custom_working_days && parseFloat(formData.custom_working_days) > 31) {
        setError('Working days cannot exceed 31 days in a month');
        return false;
      }
      if (useCustomHours && formData.custom_hours_per_day) {
        if (parseFloat(formData.custom_hours_per_day) <= 0) {
          setError('Hours per day must be greater than 0');
          return false;
        }
        if (parseFloat(formData.custom_hours_per_day) > 24) {
          setError('Hours per day cannot exceed 24');
          return false;
        }
      }
    }
    
    return true;
  };

  /**
   * Calculate total approved deductions for the month
   */
  const calculateTotalApprovedDeductions = useCallback(() => {
    const approvedDeductions = employeeDeductions.filter(
      d => getNumericStatus(d) === 1
    );
    const total = approvedDeductions.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    console.debug(`[SalaryCalculator] Total approved deductions: ${formatCurrency(total)}`);
    return total;
  }, [employeeDeductions]);

 const handleCalculate = async () => {
  if (!validateForm()) return;

  logSecurityEvent('SALARY_CALCULATE_ATTEMPT', {
    staff_name: formData.staff_name,
    staff_type: activeTab === 0 ? 'old' : 'new',
    base_salary: formData.base_salary
  });

  setLoading(true);
  setError(null);
  const startTime = performance.now();

  try {
    const currentStaffType = activeTab === 0 ? 'old' : 'new';
    
    // REMOVED: const totalApprovedDeductions = calculateTotalApprovedDeductions();
    
    const payload = {
      staff_id: formData.staff_id || (selectedEmployee?.staff_id || selectedEmployee?.id) || null,
      staff_name: sanitizeString(selectedEmployee ? formData.staff_name : formData.staff_name),
      base_salary: parseFloat(formData.base_salary),
      staff_type: currentStaffType,
      total_late_minutes: parseInt(formData.total_late_minutes) || 0,
      payroll_month: formData.payroll_month,
      payroll_month_num: formData.payroll_month_num,
      payroll_year: formData.payroll_year,
      // REMOVED: approved_deductions_total: totalApprovedDeductions,
    };
    
    if (useCustomHours && formData.custom_hours_per_day) {
      payload.working_hours_per_day = parseFloat(formData.custom_hours_per_day);
    }
    
    if (currentStaffType === 'old') {
      if (formData.custom_working_days) {
        payload.custom_working_days = parseInt(formData.custom_working_days);
      }
    } else {
      if (formData.employment_start_date) {
        payload.employment_start_date = formData.employment_start_date;
      } else {
        setError('Employment start date is required for new staff');
        setLoading(false);
        return;
      }
    }

    console.debug('[SalaryCalculator] Sending payload:', JSON.stringify(payload, null, 2));

    const response = await fetch(`${API_BASE_URL}/Salary_Prorate.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (data.success) {
      const fullSalary = parseFloat(formData.base_salary);
      const effectiveSalary = data.data.rates?.effective_salary || fullSalary;
      const proratedDeductionAmount = Math.max(0, fullSalary - effectiveSalary);
      const latenessAmount = data.data.deduction?.deduction_amount || 0;
      
      console.debug(`[SalaryCalculator] Prorated calculation: Full Salary=${formatCurrency(fullSalary)}, Effective=${formatCurrency(effectiveSalary)}, Prorated Deduction=${formatCurrency(proratedDeductionAmount)}`);
      
      const enhancedData = {
        ...data.data,
        // REMOVED: approved_deductions: employeeDeductions.filter(d => getNumericStatus(d) === 1),
        // REMOVED: approved_deductions_total: totalApprovedDeductions,
        // REMOVED: pending_deductions: employeeDeductions.filter(d => getNumericStatus(d) === 0),
        total_deductions_amount: latenessAmount, // Only lateness + prorated? No, separate
        prorated_deduction_amount: proratedDeductionAmount,
        lateness_deduction_amount: latenessAmount,
        full_salary: fullSalary,
        effective_salary: effectiveSalary,
        // FIXED: Only prorated + lateness, NO database deductions
        amount_to_deduct: currentStaffType === 'new' 
          ? proratedDeductionAmount + latenessAmount 
          : latenessAmount,  // REMOVED: + totalApprovedDeductions
      };
      
      setResult(enhancedData);
      setLastCalculationResult(enhancedData);
      setCalculationPerformed(true);
      
      saveToRecent({
        staff_id: formData.staff_id || (selectedEmployee?.staff_id || selectedEmployee?.id),
        staff_name: formData.staff_name,
        staff_type: currentStaffType === 'new' ? 'New Staff' : 'Existing Staff',
        base_salary: fullSalary,
        final_salary: data.data.final_salary, // REMOVED: - totalApprovedDeductions
        payroll_month: formData.payroll_month,
        prorated_deduction_amount: proratedDeductionAmount,
        lateness_deduction_amount: latenessAmount,
      });
      
      let message = `Salary calculated successfully! `;
      if (currentStaffType === 'new') {
        message += `Prorated deduction for days not worked: ${formatCurrency(proratedDeductionAmount)}. `;
        if (latenessAmount > 0) {
          message += `Lateness deduction: ${formatCurrency(latenessAmount)}. `;
        }
        message += `Total to deduct: ${formatCurrency(proratedDeductionAmount + latenessAmount)}. Click 'Apply Deduction' to save.`;
      } else {
        if (latenessAmount > 0) {
          message += `Lateness deduction: ${formatCurrency(latenessAmount)}. Click 'Apply Deduction' to save.`;
        } else {
          message += `No deductions to apply.`;
        }
      }
      
      setSnackbar({ open: true, message: message, severity: 'success' });
      console.debug(`[SalaryCalculator] Calculation completed in ${(performance.now() - startTime).toFixed(2)}ms`);
      logSecurityEvent('SALARY_CALCULATE_SUCCESS', { staff_name: formData.staff_name });
    } else {
      setError(data.message || 'Failed to calculate salary');
      setSnackbar({ open: true, message: data.message || 'Failed to calculate salary', severity: 'error' });
      logSecurityEvent('SALARY_CALCULATE_FAILED', { error: data.message });
    }
  } catch (err) {
    console.error('[SalaryCalculator] Network error:', err);
    setError('Network error. Please check your connection and try again.');
    setSnackbar({ open: true, message: 'Network error. Please check your connection.', severity: 'error' });
    logSecurityEvent('SALARY_CALCULATE_ERROR', { error: err.message });
  } finally {
    setLoading(false);
  }
};
  /**
   * Apply deduction to the database
   * For new staff: This includes prorated deduction (days not worked) + lateness
   * For existing staff: This includes lateness + existing approved salary deductions
   */
  const handleApplyDeduction = async () => {
    if (!calculationPerformed || !lastCalculationResult || !selectedEmployee) {
      setSnackbar({ 
        open: true, 
        message: 'Please calculate salary first before applying deduction', 
        severity: 'warning' 
      });
      return;
    }

    const amountToDeduct = lastCalculationResult.amount_to_deduct;
    
    if (amountToDeduct <= 0) {
      setSnackbar({ 
        open: true, 
        message: 'No deductions to apply. Total deduction amount is 0.', 
        severity: 'info' 
      });
      return;
    }

    setApplyDialogOpen(true);
  };

  /**
   * Confirm and create deduction record
   * This sends the appropriate amount to the deductions table
   */
 /**
 * Confirm and create deduction record
 * This sends ONLY prorated + lateness amount to the deductions table
 * NO automatic inclusion of existing salary deductions from database
 */
const confirmApplyDeduction = async () => {
  if (!applyRemark || applyRemark.trim().length < 3) {
    setSnackbar({ 
      open: true, 
      message: 'Please provide a detailed reason for the deduction (minimum 3 characters)', 
      severity: 'error' 
    });
    return;
  }

  if (!selectedEmployee || !lastCalculationResult) {
    setSnackbar({ 
      open: true, 
      message: 'Missing employee or calculation data', 
      severity: 'error' 
    });
    return;
  }

  setApplyingDeduction(true);
  
  try {
    const amountToDeduct = lastCalculationResult.amount_to_deduct;
    const proratedAmount = lastCalculationResult.prorated_deduction_amount || 0;
    const latenessAmount = lastCalculationResult.lateness_deduction_amount || 0;
    const staffType = activeTab === 0 ? 'Existing Staff' : 'New Staff';
    const startDate = activeTab === 1 ? formData.employment_start_date : 'N/A';
    
    // Build detailed remark explaining the deduction breakdown (NO database deductions)
    let detailedRemark = `[AUTOMATED FROM CALCULATOR] ${applyRemark.trim()}
        
STAFF TYPE: ${staffType}
Period: ${MONTHS.find(m => m.value === formData.payroll_month_num)?.label} ${formData.payroll_year}
Staff: ${formData.staff_name}
Full Salary: ${formatCurrency(lastCalculationResult.full_salary)}
Effective Salary: ${formatCurrency(lastCalculationResult.effective_salary)}`;

    if (staffType === 'New Staff') {
      detailedRemark += `
        
PRORATED DEDUCTION BREAKDOWN:
- Employment Start Date: ${startDate}
- Days/Month worked: ${lastCalculationResult.calculation_constants?.working_days_per_month || 'N/A'} days
- Days Not Worked: ${lastCalculationResult.calculation_constants?.total_days_in_month - lastCalculationResult.calculation_constants?.working_days_per_month || 'N/A'} days
- Prorated Deduction (days not worked): ${formatCurrency(proratedAmount)}`;
    }

    detailedRemark += `
        
DEDUCTION BREAKDOWN:
- Lateness Deduction: ${formatCurrency(latenessAmount)} (from ${formData.total_late_minutes || 0} minutes late)
- TOTAL DEDUCTION AMOUNT: ${formatCurrency(amountToDeduct)}

Note: This deduction is specific to the selected payroll month only.
Note: This deduction includes ONLY prorated (for new staff) and lateness deductions.
Existing salary deductions from the database are NOT automatically included.`;

    let requestedBy = 'HR';
    try {
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const profile = JSON.parse(userProfile);
        requestedBy = profile.name || profile.full_name || 'HR';
      }
    } catch (e) {
      console.error('[SalaryCalculator] Error parsing user profile:', e);
    }
    
    const deductionData = {
      phone: selectedEmployee.phone,
      amount: amountToDeduct,
      remark: sanitizeString(detailedRemark),
      payroll_month: parseInt(formData.payroll_month_num),
      payroll_year: parseInt(formData.payroll_year),
      requested_by: requestedBy
    };
    
    console.debug('[SalaryCalculator] Creating deduction record:', deductionData);
    
    const response = await fetch(`${API_BASE_URL}/salary_deductions.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deductionData)
    });
    
    const result = await response.json();
    
    if (result.success) {
      let successMessage = `✓ Deduction applied successfully! `;
      if (activeTab === 1 && proratedAmount > 0) {
        successMessage += `Prorated deduction for days not worked: ${formatCurrency(proratedAmount)}. `;
      }
      if (latenessAmount > 0) {
        successMessage += `Lateness deduction: ${formatCurrency(latenessAmount)}. `;
      }
      if (proratedAmount === 0 && latenessAmount === 0) {
        successMessage = `No deductions to apply. Amount is 0.`;
      } else {
        successMessage += `Total: ${formatCurrency(amountToDeduct)}.`;
      }
      
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      
      // Refresh deductions list to show the newly created deduction (optional)
      if (selectedEmployee && (selectedEmployee.id || selectedEmployee.staff_id)) {
        await fetchEmployeeDeductions(
          selectedEmployee.id || selectedEmployee.staff_id,
          formData.payroll_month_num,
          formData.payroll_year
        );
      }
      
      setApplyDialogOpen(false);
      setApplyRemark('');
      
      logSecurityEvent('DEDUCTION_APPLIED_FROM_CALCULATOR', {
        staff_id: selectedEmployee.id,
        staff_name: formData.staff_name,
        amount: amountToDeduct,
        prorated_amount: proratedAmount,
        lateness_amount: latenessAmount,
        period: `${formData.payroll_month_num}/${formData.payroll_year}`,
        staff_type: staffType
      });
    } else {
      setSnackbar({ 
        open: true, 
        message: result.message || 'Failed to create deduction record', 
        severity: 'error' 
      });
    }
  } catch (err) {
    console.error('[SalaryCalculator] Error applying deduction:', err);
    setSnackbar({ 
      open: true, 
      message: err.response?.data?.message || 'Failed to apply deduction', 
      severity: 'error' 
    });
  } finally {
    setApplyingDeduction(false);
  }
};

  const handleReset = () => {
    const newStaffType = activeTab === 0 ? 'old' : 'new';
    setFormData({
      staff_id: '',
      staff_name: '',
      staff_phone: '',
      staff_email: '',
      staff_department: '',
      base_salary: '',
      total_late_minutes: '',
      payroll_month: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
      payroll_month_num: new Date().getMonth() + 1,
      payroll_year: new Date().getFullYear(),
      staff_type: newStaffType,
      custom_working_days: '',
      custom_hours_per_day: '',
      employment_start_date: new Date().toISOString().split('T')[0],
    });
    setSelectedEmployee(null);
    setSearchInput('');
    setEmployeeDeductions([]);
    setResult(null);
    setError(null);
    setCalculationPerformed(false);
    setLastCalculationResult(null);
    setUseCustomHours(false);
    setSnackbar({ open: true, message: 'Form reset successfully', severity: 'info' });
    console.debug('[SalaryCalculator] Form reset');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const loadRecentCalculation = (calc) => {
    setFormData(prev => ({
      ...prev,
      staff_name: calc.staff_name,
      staff_id: calc.staff_id || '',
      base_salary: calc.base_salary.toString(),
      payroll_month: calc.payroll_month,
    }));
    setResult(null);
    setError(null);
    setCalculationPerformed(false);
    setLastCalculationResult(null);
    setSnackbar({ open: true, message: `Loaded calculation for ${calc.staff_name}`, severity: 'info' });
    console.debug(`[SalaryCalculator] Loaded recent calculation for: ${calc.staff_name}`);
  };

  const CustomPopper = (props) => (
    <Popper {...props} placement="bottom-start" style={{ width: props.style?.width, zIndex: 1300 }}>
      <Paper elevation={4} sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
        {searchLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ mt: 1 }}>Searching employees...</Typography>
          </Box>
        )}
        {!searchLoading && searchError && !searchResults.length && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="error">{searchError}</Typography>
          </Box>
        )}
        {!searchLoading && !searchError && searchResults.length === 0 && searchInput.length >= 2 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No employees found</Typography>
          </Box>
        )}
        {props.children}
      </Paper>
    </Popper>
  );

  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{ 
      borderRadius: 3, 
      height: '100%', 
      boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>{title}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: color }}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ 
        flexGrow: 1, 
        p: 3, 
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5, 
        maxWidth: '1400px', 
        mx: 'auto', 
        width: '100%', 
        overflow: 'auto', 
        height: '100vh' 
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => console.debug('[SalaryCalculator] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />

        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          background: 'linear-gradient(135deg, #C84646 0%, #a33a3a 100%)', 
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Salary Calculator
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
                Calculate prorated salary based on actual working days, lateness deductions, and approved salary deductions
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
                <strong>Note:</strong> For new staff, prorated deduction (days not worked) is automatically calculated and included
              </Typography>
            </Box>
          </Box>
        </Paper>

        {recentCalculations.length > 0 && (
          <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: '#fafafa', 
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
              onClick={() => setShowRecent(!showRecent)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon sx={{ color: '#C84646' }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  Recent Calculations
                </Typography>
                <Chip size="small" label={`${recentCalculations.length}`} sx={{ bgcolor: '#C84646', color: 'white' }} />
              </Box>
              <ExpandMoreIcon sx={{ transform: showRecent ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
            </Box>
            {showRecent && (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {recentCalculations.map((calc) => (
                    <Grid item xs={12} sm={6} md={4} key={calc.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer', 
                          '&:hover': { bgcolor: '#f5f5f5', transform: 'translateY(-2px)', transition: 'all 0.2s' } 
                        }}
                        onClick={() => loadRecentCalculation(calc)}
                      >
                        <CardContent>
                          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                            {new Date(calc.timestamp).toLocaleDateString()}
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                            {calc.staff_name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Chip size="small" label={calc.staff_type} sx={{ bgcolor: '#C84646', color: 'white' }} />
                            <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                              {formatCurrency(calc.final_salary)}
                            </Typography>
                          </Box>
                          {calc.prorated_deduction_amount > 0 && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                              Prorated: {formatCurrency(calc.prorated_deduction_amount)}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        )}

        <Paper sx={{ mb: 3, borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                py: 2,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontFamily: 'Times New Roman, serif',
              },
              '& .Mui-selected': {
                color: '#C84646',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#C84646',
              }
            }}
          >
            <Tab icon={<PersonIcon />} label="Existing Staff" iconPosition="start" />
            <Tab icon={<PersonAddIcon />} label="New Staff (Rolling Pattern)" iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#C84646', fontFamily: 'Times New Roman, serif' }}>
                Search Employee (Optional - Auto-populates staff details)
              </Typography>
              
              <Autocomplete
                freeSolo
                options={searchResults}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.surname || ''} ${option.other_names || ''} (${option.phone || ''})`;
                }}
                value={selectedEmployee}
                onChange={handleEmployeeSelect}
                inputValue={searchInput}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setSearchInput(newInputValue);
                    if (!newInputValue) {
                      handleClearSelection();
                    }
                  }
                }}
                loading={searchLoading}
                PopperComponent={CustomPopper}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type employee name or phone number (min. 2 characters)..."
                    variant="outlined"
                    error={!!searchError}
                    helperText={searchError || "Search to auto-fill staff details and view existing deductions"}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#C84646' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {searchLoading && <CircularProgress size={20} />}
                          {selectedEmployee && (
                            <IconButton size="small" onClick={handleClearSelection} sx={{ mr: 1 }}>
                              <ClearIcon />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const hasSalary = (option.salary_amount || option.base_salary) > 0;
                  return (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: hasSalary ? '#4caf50' : '#ff9800', width: 40, height: 40 }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {option.surname} {option.other_names}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip size="small" icon={<BusinessIcon sx={{ fontSize: 14 }} />} label={option.department || 'No Dept'} variant="outlined" />
                              <Chip size="small" icon={<PhoneIcon sx={{ fontSize: 14 }} />} label={option.phone} variant="outlined" />
                              <Chip 
                                size="small" 
                                label={formatCurrency(option.salary_amount || option.base_salary)} 
                                sx={{ bgcolor: hasSalary ? '#e8f5e9' : '#fff3e0', color: hasSalary ? '#2e7d32' : '#ed6c02' }}
                              />
                            </Box>
                          </Box>
                        </Box>
                        <Chip size="small" label={option.status === 'Active' ? 'Active' : 'Inactive'} color={option.status === 'Active' ? 'success' : 'default'} />
                      </Box>
                    </li>
                  );
                }}
              />
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Staff ID"
                  name="staff_id"
                  value={formData.staff_id}
                  onChange={handleInputChange}
                  placeholder={selectedEmployee ? (selectedEmployee.staff_id || 'Auto-filled') : "EMP001"}
                  variant="outlined"
                  disabled={!!selectedEmployee}
                  InputProps={{ readOnly: !!selectedEmployee }}
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                  InputLabelProps={{ sx: { fontFamily: 'Times New Roman, serif' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Staff Name"
                  name="staff_name"
                  value={formData.staff_name}
                  onChange={handleInputChange}
                  placeholder="Enter staff full name"
                  variant="outlined"
                  error={error && !formData.staff_name}
                  helperText={error && !formData.staff_name ? "Staff name is required" : ""}
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                  InputLabelProps={{ sx: { fontFamily: 'Times New Roman, serif' } }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="staff_phone"
                  value={formData.staff_phone}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={!!selectedEmployee}
                  InputProps={{ readOnly: !!selectedEmployee }}
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Department"
                  name="staff_department"
                  value={formData.staff_department}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={!!selectedEmployee}
                  InputProps={{ readOnly: !!selectedEmployee }}
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Payroll Month *</InputLabel>
                  <Select
                    value={formData.payroll_month_num}
                    onChange={handlePayrollMonthChange}
                    label="Payroll Month *"
                    sx={{ fontFamily: 'Times New Roman, serif' }}
                  >
                    {MONTHS.map(month => (
                      <MenuItem key={month.value} value={month.value} sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Payroll Year *</InputLabel>
                  <Select
                    value={formData.payroll_year}
                    onChange={handlePayrollYearChange}
                    label="Payroll Year *"
                    sx={{ fontFamily: 'Times New Roman, serif' }}
                  >
                    {years.map(year => (
                      <MenuItem key={year} value={year} sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {activeTab === 0 && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Working Days per Month (Optional)"
                      name="custom_working_days"
                      type="number"
                      value={formData.custom_working_days}
                      onChange={handleInputChange}
                      placeholder="Leave empty for standard 22 days"
                      variant="outlined"
                      helperText="Standard is 22 working days (Monday-Friday)"
                      sx={{ fontFamily: 'Times New Roman, serif' }}
                      InputLabelProps={{ sx: { fontFamily: 'Times New Roman, serif' } }}
                      InputProps={{ inputProps: { min: 1, max: 31 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={useCustomHours} 
                          onChange={(e) => setUseCustomHours(e.target.checked)} 
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#C84646' } }}
                        />
                      }
                      label="Customize Working Hours"
                      sx={{ fontFamily: 'Times New Roman, serif' }}
                    />
                    {useCustomHours && (
                      <TextField
                        fullWidth
                        label="Hours per Working Day"
                        name="custom_hours_per_day"
                        type="number"
                        value={formData.custom_hours_per_day}
                        onChange={handleInputChange}
                        placeholder="Default: 9 hours"
                        variant="outlined"
                        sx={{ mt: 1, fontFamily: 'Times New Roman, serif' }}
                        InputLabelProps={{ sx: { fontFamily: 'Times New Roman, serif' } }}
                        InputProps={{ inputProps: { min: 1, max: 24, step: 0.5 } }}
                        helperText="Default is 9 hours (540 minutes per day)"
                      />
                    )}
                  </Grid>
                </>
              )}
              
              {activeTab === 1 && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Employment Start Date"
                    name="employment_start_date"
                    type="date"
                    value={formData.employment_start_date}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <CalendarIcon sx={{ mr: 1, color: '#666' }} />,
                    }}
                    helperText="First day of employment - determines working pattern and prorated salary"
                    error={error && !formData.employment_start_date}
                    sx={{ fontFamily: 'Times New Roman, serif' }}
                    InputLabelProps={{ sx: { fontFamily: 'Times New Roman, serif' } }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Base Monthly Salary (₦)"
                  name="base_salary"
                  type="number"
                  value={formData.base_salary}
                  onChange={handleInputChange}
                  placeholder="e.g., 300000"
                  variant="outlined"
                  InputProps={{
                    startAdornment: <MoneyIcon sx={{ mr: 1, color: '#666' }} />,
                  }}
                  error={error && (!formData.base_salary || parseFloat(formData.base_salary) <= 0)}
                  helperText={error && !formData.base_salary ? "Salary is required" : "Monthly salary before deductions"}
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                  InputLabelProps={{ sx: { fontFamily: 'Times New Roman, serif' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Late Minutes"
                  name="total_late_minutes"
                  type="number"
                  value={formData.total_late_minutes}
                  onChange={handleInputChange}
                  placeholder="e.g., 180 (3 hours)"
                  variant="outlined"
                  InputProps={{
                    startAdornment: <AccessTimeIcon sx={{ mr: 1, color: '#666' }} />,
                  }}
                  helperText="Each minute is deducted at the calculated minute rate"
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                  InputLabelProps={{ sx: { fontFamily: 'Times New Roman, serif' } }}
                />
              </Grid>
            </Grid>

            {selectedEmployee && employeeDeductions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'Times New Roman, serif' }}>
                    <ReceiptIcon sx={{ color: '#C84646' }} />
                    Existing Salary Deductions for {MONTHS.find(m => m.value === selectedDeductionMonth)?.label} {selectedDeductionYear}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setShowDeductionsDialog(true)}
                    sx={{ fontFamily: 'Times New Roman, serif', textTransform: 'none' }}
                  >
                    View All
                  </Button>
                </Box>
                
                {loadingDeductions ? (
                  <LinearProgress />
                ) : (
                  <>
                    {employeeDeductions.filter(d => getNumericStatus(d) === 1).length > 0 && (
                      <Alert severity="info" sx={{ mb: 2, fontFamily: 'Times New Roman, serif' }}>
                        <strong>Approved Deductions Found:</strong> {formatCurrency(
                          employeeDeductions.filter(d => getNumericStatus(d) === 1).reduce((sum, d) => sum + parseFloat(d.amount), 0)
                        )} will be deducted from salary.
                      </Alert>
                    )}
                    {employeeDeductions.length === 0 && (
                      <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        No salary deductions found for this period.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Alert severity="info" sx={{ mt: 3, borderRadius: 2, fontFamily: 'Times New Roman, serif' }}>
                <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'Times New Roman, serif' }}>
                  <ScheduleIcon /> New Staff Working Schedule & Proration
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Times New Roman, serif' }}>
                  <strong>📋 Working Rule:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                    <li><strong>First 7 days</strong> of resumption: Work CONTINUOUSLY (including weekends)</li>
                    <li><strong>After first 7 days:</strong> 1 day OFF → 2 days WORK → 1 day OFF → 2 days WORK (repeating pattern)</li>
                    <li>This gives <strong>2 off days per week</strong> but not consecutively</li>
                  </ul>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Times New Roman, serif' }}>
                  <strong>💰 Proration:</strong> Salary is prorated based on ACTUAL working days from start date to month end.
                  The <strong style={{ color: '#f44336' }}>prorated deduction (days not worked)</strong> will be sent to the deductions table.
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  <strong>⏰ Lateness:</strong> Each minute late is deducted at the calculated minute rate.
                </Typography>
              </Alert>
            )}

            {activeTab === 0 && (
              <Alert severity="info" sx={{ mt: 3, borderRadius: 2, fontFamily: 'Times New Roman, serif' }}>
                <AlertTitle sx={{ fontFamily: 'Times New Roman, serif' }}>📋 Existing Staff Schedule</AlertTitle>
                <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  <strong>Standard:</strong> 22 working days per month (Monday-Friday)<br />
                  <strong>Hours:</strong> 9 hours per day (540 minutes) - customizable<br />
                  <strong>Lateness:</strong> Each minute deducted at calculated minute rate<br />
                  <strong>Salary Deductions:</strong> Approved deductions are automatically applied
                </Typography>
              </Alert>
            )}
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2, fontFamily: 'Times New Roman, serif' }} onClose={() => setError(null)}>
            <AlertTitle sx={{ fontFamily: 'Times New Roman, serif' }}>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {result && (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: '#4CAF50', color: 'white', borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
                      Full Salary
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      {formatCurrency(result.full_salary)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      Monthly rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: '#2196F3', color: 'white', borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
                      Effective Salary
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      {formatCurrency(result.effective_salary)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      Based on {result.calculation_constants?.working_days_per_month || 0} working days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: '#ff9800', color: 'white', borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
                      Prorated Deduction
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      {formatCurrency(result.prorated_deduction_amount)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      Days not worked
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: '#f44336', color: 'white', borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
                      Lateness Deduction
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      {formatCurrency(result.lateness_deduction_amount)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      From {result.lateness_breakdown?.total_late_minutes || 0} minutes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

        
<Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#e8f5e9', border: '2px solid #4caf50' }}>
  <CardContent>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
        Final Net Salary:
      </Typography>
      <Typography variant="h3" fontWeight="bold" color="#4caf50" sx={{ fontFamily: 'monospace' }}>
        {formatCurrency(result.final_salary)}  {/* REMOVED: - (result.approved_deductions_total || 0) */}
      </Typography>
    </Box>
    <Typography variant="caption" color="textSecondary">
      Full Salary - Prorated Deduction - Lateness Deduction
    </Typography>
  </CardContent>
</Card>
            {result.correction_info?.applied && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, fontFamily: 'Times New Roman, serif' }} icon={<WarningIcon />}>
                <AlertTitle sx={{ fontFamily: 'Times New Roman, serif' }}>Auto-Correction Applied</AlertTitle>
                {result.correction_info.reason}
              </Alert>
            )}

            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'Times New Roman, serif' }}>
                <InfoIcon sx={{ color: '#C84646' }} />
                Detailed Calculation Breakdown
              </Typography>

              <Accordion expanded={expandedSections.workingDays} onChange={() => toggleSection('workingDays')} sx={{ '&:before': { display: 'none' }, mb: 1, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>📅 Working Days Calculation</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '40%', fontFamily: 'Times New Roman, serif' }}>Staff Type:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>
                          <Chip label={result.staff_type} color={result.staff_type === 'New Staff' ? 'primary' : 'default'} size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Working Days per Month:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}><strong>{result.calculation_constants?.working_days_per_month || 'N/A'} days</strong></TableCell>
                      </TableRow>
                      {result.working_days_breakdown && (
                        <>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Calculation Method:</TableCell>
                            <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{result.working_days_breakdown.calculation_method}</TableCell>
                          </TableRow>
                          {result.working_days_breakdown.employment_start_date && (
                            <>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Employment Start Date:</TableCell>
                                <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{result.working_days_breakdown.employment_start_date}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Days Not Worked:</TableCell>
                                <TableCell sx={{ fontFamily: 'Times New Roman, serif', color: '#f44336' }}>{result.calculation_constants?.total_days_in_month - result.calculation_constants?.working_days_per_month} days</TableCell>
                              </TableRow>
                            </>
                          )}
                          {result.working_days_breakdown.pattern_description && (
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Working Pattern:</TableCell>
                              <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{result.working_days_breakdown.pattern_description}</TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>

              {activeTab === 1 && result.working_days_breakdown?.complete_daily_breakdown && (
                <Accordion expanded={expandedSections.dailyBreakdown} onChange={() => toggleSection('dailyBreakdown')} sx={{ '&:before': { display: 'none' }, mb: 1, borderRadius: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>📆 Daily Breakdown</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableBody>
                          {result.working_days_breakdown.complete_daily_breakdown.map((day, idx) => (
                            <TableRow key={idx} sx={{ bgcolor: day.worked ? '#e8f5e9' : '#ffebee' }}>
                              <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{day.date}</TableCell>
                              <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{day.day}</TableCell>
                              <TableCell>
                                <Chip label={day.worked ? 'WORK' : 'OFF'} size="small" color={day.worked ? 'success' : 'error'} variant="outlined" />
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.75rem', color: '#666', fontFamily: 'Times New Roman, serif' }}>{day.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion expanded={expandedSections.rates} onChange={() => toggleSection('rates')} sx={{ '&:before': { display: 'none' }, mb: 1, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>💰 Rate Calculations</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '40%', fontFamily: 'Times New Roman, serif' }}>Full Monthly Salary:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{formatCurrency(result.rates?.base_salary)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Prorated Deduction:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif', color: '#f44336' }}>- {formatCurrency(result.prorated_deduction_amount)}</TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Effective Salary:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}><strong>{formatCurrency(result.effective_salary)}</strong></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Daily Rate:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{formatCurrency(result.rates?.daily_rate)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Hourly Rate:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{formatCurrency(result.rates?.hourly_rate)}</TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>⭐ Minute Rate:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}><strong>{formatCurrency(result.rates?.minute_rate)}</strong></TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}><em>Per minute - Used for lateness</em></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedSections.lateness} onChange={() => toggleSection('lateness')} sx={{ '&:before': { display: 'none' }, mb: 1, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>⏰ Lateness Breakdown</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '40%', fontFamily: 'Times New Roman, serif' }}>Total Late Minutes:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{result.lateness_breakdown?.total_late_minutes?.toLocaleString() || 0} minutes</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Lateness Deduction:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif', color: '#f44336' }}>{formatCurrency(result.lateness_deduction_amount)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedSections.deduction} onChange={() => toggleSection('deduction')} sx={{ '&:before': { display: 'none' }, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>💸 Total Deduction to Apply</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '40%', fontFamily: 'Times New Roman, serif' }}>Prorated Deduction (Days not worked):</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif', color: '#f44336' }}>{formatCurrency(result.prorated_deduction_amount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Lateness Deduction:</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif', color: '#f44336' }}>+ {formatCurrency(result.lateness_deduction_amount)}</TableCell>
                      </TableRow>
                      {result.approved_deductions_total > 0 && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Existing Salary Deductions:</TableCell>
                          <TableCell sx={{ fontFamily: 'Times New Roman, serif', color: '#f44336' }}>+ {formatCurrency(result.approved_deductions_total)}</TableCell>
                        </TableRow>
                      )}
                      <Divider />
                      <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Total Deduction Amount:</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.2em', color: '#f44336', fontFamily: 'monospace' }}>
                          {formatCurrency(result.amount_to_deduct)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>

              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: '#e0e0e0' }}>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  Payroll for {result.payroll_month} | Staff: {result.staff_name}
                  {result.staff_id && ` (ID: ${result.staff_id})`} | Type: {result.staff_type}
                </Typography>
              </Box>
            </Paper>
          </>
        )}

        <Paper sx={{ p: 3, borderRadius: 3, mb: 3, position: 'sticky', bottom: 0, bgcolor: 'white', zIndex: 10 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={handleReset}
              disabled={loading}
              sx={{ fontFamily: 'Times New Roman, serif', textTransform: 'none', minWidth: 120 }}
            >
              Reset
            </Button>
            <Button 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />} 
              onClick={handleCalculate}
              disabled={loading}
              sx={{ bgcolor: '#C84646', '&:hover': { bgcolor: '#a33a3a' }, fontFamily: 'Times New Roman, serif', textTransform: 'none', minWidth: 160 }}
            >
              {loading ? 'Calculating...' : 'Calculate Salary'}
            </Button>
            <Button 
              variant="contained" 
              startIcon={<SaveIcon />} 
              onClick={handleApplyDeduction}
              disabled={!calculationPerformed || loading || applyingDeduction}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, fontFamily: 'Times New Roman, serif', textTransform: 'none', minWidth: 180 }}
            >
              {applyingDeduction ? <CircularProgress size={20} /> : 'Apply Deduction'}
            </Button>
          </Box>
          {calculationPerformed && lastCalculationResult && (
            <Typography variant="caption" align="center" sx={{ display: 'block', mt: 1, color: '#4caf50' }}>
              ✓ Total deduction to apply: {formatCurrency(lastCalculationResult.amount_to_deduct)}. Click "Apply Deduction" to save to database.
            </Typography>
          )}
        </Paper>

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            Designed by Elegantè IT - Salary Calculator System
          </Typography>
        </footer>
      </Box>

      <Dialog open={showDeductionsDialog} onClose={() => setShowDeductionsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#C84646', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon /> Salary Deductions for {selectedEmployee ? selectedEmployee.full_name || formData.staff_name : formData.staff_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Month</InputLabel>
                <Select value={selectedDeductionMonth} onChange={(e) => handleDeductionPeriodChange(e.target.value, selectedDeductionYear)} label="Month">
                  {MONTHS.map(month => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Year</InputLabel>
                <Select value={selectedDeductionYear} onChange={(e) => handleDeductionPeriodChange(selectedDeductionMonth, e.target.value)} label="Year">
                  {years.map(year => (<MenuItem key={year} value={year}>{year}</MenuItem>))}
                </Select>
              </FormControl>
              <Button size="small" variant="outlined" onClick={() => { const current = new Date(); handleDeductionPeriodChange(current.getMonth() + 1, current.getFullYear()); }}>
                Current Month
              </Button>
            </Box>

            {loadingDeductions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : employeeDeductions.length === 0 ? (
              <Alert severity="info">No salary deductions found for {MONTHS.find(m => m.value === selectedDeductionMonth)?.label} {selectedDeductionYear}.</Alert>
            ) : (
              <>
                {employeeDeductions.filter(d => getNumericStatus(d) === 0).length > 0 && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#ff9800', fontFamily: 'Times New Roman, serif' }}>Pending Approval</Typography>
                    <List>
                      {employeeDeductions.filter(d => getNumericStatus(d) === 0).map((deduction) => (
                        <ListItem key={deduction.id} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                          <ListItemAvatar><Avatar sx={{ bgcolor: '#ff9800' }}><PendingIcon /></Avatar></ListItemAvatar>
                          <ListItemText primary={<Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body1" fontWeight="bold">{formatCurrency(deduction.amount)}</Typography><Chip size="small" label="Pending" sx={{ bgcolor: '#ff9800', color: 'white' }} /></Box>} secondary={<><Typography variant="body2" color="textSecondary"><strong>Reason:</strong> {deduction.remark || 'No reason provided'}</Typography><Typography variant="caption" color="textSecondary">Created: {formatDate(deduction.date_created)}</Typography></>} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
                {employeeDeductions.filter(d => getNumericStatus(d) === 1).length > 0 && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#4caf50', fontFamily: 'Times New Roman, serif' }}>Approved (Will be deducted)</Typography>
                    <List>
                      {employeeDeductions.filter(d => getNumericStatus(d) === 1).map((deduction) => (
                        <ListItem key={deduction.id} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                          <ListItemAvatar><Avatar sx={{ bgcolor: '#4caf50' }}><CheckCircleIcon /></Avatar></ListItemAvatar>
                          <ListItemText primary={<Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body1" fontWeight="bold" color="error">-{formatCurrency(deduction.amount)}</Typography><Chip size="small" label="Approved" sx={{ bgcolor: '#4caf50', color: 'white' }} /></Box>} secondary={<><Typography variant="body2" color="textSecondary"><strong>Reason:</strong> {deduction.remark || 'No reason provided'}</Typography><Typography variant="caption" color="textSecondary">Approved by: {deduction.approved_by || 'N/A'} on {formatDate(deduction.approval_date)}</Typography></>} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setShowDeductionsDialog(false)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={applyDialogOpen} onClose={() => !applyingDeduction && setApplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SaveIcon /> Apply Salary Deduction</Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
              <strong>Staff:</strong> {formData.staff_name}<br />
              <strong>Period:</strong> {MONTHS.find(m => m.value === formData.payroll_month_num)?.label} {formData.payroll_year}<br />
              <strong>Total Deduction Amount:</strong> {formatCurrency(lastCalculationResult?.amount_to_deduct || 0)}<br />
              <strong>Breakdown:</strong><br />
              &nbsp;&nbsp;• Prorated (Days not worked): {formatCurrency(lastCalculationResult?.prorated_deduction_amount || 0)}<br />
              &nbsp;&nbsp;• Lateness: {formatCurrency(lastCalculationResult?.lateness_deduction_amount || 0)}
            </Alert>
            <TextField label="Reason for Deduction *" multiline rows={4} fullWidth required value={applyRemark} onChange={(e) => setApplyRemark(e.target.value)} placeholder="Provide a detailed reason why this deduction is being applied..." helperText="Minimum 3 characters" inputProps={{ minLength: 3 }} disabled={applyingDeduction} sx={{ fontFamily: 'Times New Roman, serif' }} />
            <Alert severity="warning" sx={{ fontFamily: 'Times New Roman, serif' }}><strong>Important:</strong> This will create a pending deduction record that requires approval before being applied to payroll.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)} disabled={applyingDeduction}>Cancel</Button>
          <Button onClick={confirmApplyDeduction} variant="contained" sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }} disabled={applyingDeduction || !applyRemark || applyRemark.trim().length < 3}>
            {applyingDeduction ? <CircularProgress size={24} /> : 'Confirm Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled" sx={{ fontFamily: 'Times New Roman, serif' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalaryCalculator;